import React, { useEffect, useId, useMemo, useState } from "react";
import { Search, X } from "lucide-react";

const searchableText = (patient) => [
  patient?.name,
  patient?.phone,
  patient?.email,
  patient?.patient_id,
].filter(Boolean).join(" ").toLowerCase();

export function PatientSearchSelect({
  patients = [],
  value = "",
  onChange,
  label = "Patient",
  placeholder = "Search by name, mobile, email or patient ID…",
  required = false,
  testid,
  className = "",
  onSearch,
  disabled = false,
}) {
  const inputId = useId();
  const [query, setQuery] = useState("");
  const [remotePatients, setRemotePatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searching, setSearching] = useState(false);
  const candidates = useMemo(() => {
    const byId = new Map();
    [...remotePatients, ...patients].forEach((patient) => {
      if (patient?.patient_id) byId.set(patient.patient_id, patient);
    });
    return [...byId.values()];
  }, [patients, remotePatients]);
  const selected = candidates.find((patient) => patient.patient_id === value)
    || (selectedPatient?.patient_id === value ? selectedPatient : null);

  useEffect(() => {
    if (!onSearch || query.trim().length < 2) {
      setRemotePatients([]);
      setSearching(false);
      return undefined;
    }
    let active = true;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const rows = await onSearch(query.trim());
        if (active) setRemotePatients(Array.isArray(rows) ? rows : []);
      } catch {
        if (active) setRemotePatients([]);
      } finally {
        if (active) setSearching(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [onSearch, query]);

  const matches = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return candidates.slice(0, 20);
    return candidates.filter((patient) => searchableText(patient).includes(normalized)).slice(0, 50);
  }, [candidates, query]);

  return <div className={className} data-testid={testid}>
    {label && <label htmlFor={inputId} className="text-xs font-bold text-slate-600 block mb-1">{label}{required ? " *" : ""}</label>}
    {selected ? <div className="flex items-center justify-between gap-3 border border-emerald-200 bg-emerald-50 rounded-lg px-3 py-2">
      <div className="min-w-0">
        <div className="font-semibold text-sm text-mg-navy truncate">{selected.name || "Unnamed patient"}</div>
        <div className="text-xs text-slate-500 truncate">{selected.phone || "No mobile"}{selected.email ? ` · ${selected.email}` : ""} · {selected.patient_id}</div>
      </div>
      {!disabled && <button type="button" aria-label="Clear selected patient" onClick={() => { onChange(""); setSelectedPatient(null); setQuery(""); }} className="p-1 text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>}
    </div> : <>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
        <input id={inputId} data-testid={testid ? `${testid}-search` : undefined} className="mg-input w-full pl-9" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={placeholder} aria-label={`${label || "Patient"} search`} disabled={disabled} />
      </div>
      {(query || candidates.length > 0) && <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto mt-1 bg-white shadow-sm">
        {searching && <div className="px-3 py-2 text-xs text-slate-500">Searching all patients…</div>}
        {matches.map((patient) => <button key={patient.patient_id} type="button" onClick={() => { setSelectedPatient(patient); onChange(patient.patient_id, patient); setQuery(""); }} className="block w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0">
          <div className="font-semibold text-sm text-mg-navy">{patient.name || "Unnamed patient"}</div>
          <div className="text-xs text-slate-500">{patient.phone || "No mobile"}{patient.email ? ` · ${patient.email}` : ""} · {patient.patient_id}</div>
        </button>)}
        {!searching && !matches.length && <div className="px-3 py-4 text-sm text-slate-500 text-center">No matching patient found</div>}
      </div>}
    </>}
  </div>;
}
